/**
 * Client-side image compression and thumbnail generation.
 * Uses canvas to resize large phone photos before sending to the backend.
 */

const MAX_UPLOAD_BYTES = 400 * 1024; // 400KB — matches backend MAX_IMAGE_SIZE; SDK crashes above ~500KB

/** Per-MESSAGE sum budget in BASE64 chars — mirrors the backend single source
 *  (backend/src/voice/image-budget.util.ts → TOTAL_IMAGE_BUDGET, issue #1165).
 *  All images of one message land on ONE JSON stdin line and the Claude SDK
 *  subprocess crashes at ~500KB per line, so the SUMMED base64 of all
 *  attachments must stay ≤ 400KB — the ~100KB gap is the JSON-envelope +
 *  prompt-text headroom. Keep in lockstep with the backend constant (guarded
 *  by setpoint-evals/multimodal-sum-budget/SE-3-cross-side-parity). */
export const TOTAL_IMAGE_BUDGET_B64 = MAX_UPLOAD_BYTES;

/** Decoded-bytes equivalent of the base64 sum budget (base64 inflates 4/3).
 *  Canvas recompression targets measure decoded bytes, so shares are computed
 *  in this unit: N images each ≤ floor(300KB/N) decoded ⇒ sum ≤ 400KB base64. */
const TOTAL_IMAGE_BUDGET_DECODED = Math.floor((TOTAL_IMAGE_BUDGET_B64 * 3) / 4);

export interface ImageBudgetVerdict {
  totalBytes: number;
  budgetBytes: number;
  allowed: boolean;
}

/** Pure per-message SUM budget check over base64 lengths (`data.length`) —
 *  frontend mirror of the backend `checkTotalImageBudget`. */
export function checkTotalImageBudget(
  base64Lengths: number[],
  budgetBytes: number = TOTAL_IMAGE_BUDGET_B64,
): ImageBudgetVerdict {
  const totalBytes = base64Lengths.reduce((sum, n) => sum + n, 0);
  return { totalBytes, budgetBytes, allowed: totalBytes <= budgetBytes };
}

/** Per-image recompression budget (decoded bytes) when N images share one
 *  message: each image gets an equal share of the per-message budget so the
 *  aggregate stays within TOTAL_IMAGE_BUDGET_B64 for ANY N (recompress-harder
 *  strategy — more images ⇒ smaller per-image target). */
export function perImageBudget(numImages: number): number {
  const n = Math.max(1, numImages);
  return Math.min(MAX_UPLOAD_BYTES, Math.floor(TOTAL_IMAGE_BUDGET_DECODED / n));
}

const SUPPORTED_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

export type SupportedMimeType = 'image/jpeg' | 'image/png' | 'image/gif' | 'image/webp';

export function isSupportedImage(file: File): boolean {
  return SUPPORTED_TYPES.has(file.type);
}

/** Load a File into an HTMLImageElement */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${file.name}`));
    img.src = URL.createObjectURL(file);
  });
}

/** Resize and compress an image via canvas. Returns raw base64 (no data URI prefix).
 *  `budgetBytes` defaults to the single-image cap. For multi-image turns, callers
 *  should pass `perImageBudget(numImages)` so the aggregate stays under the SDK
 *  1 MB stdin-line limit. */
export async function compressImage(
  file: File,
  maxWidth = 1920,
  quality = 0.8,
  budgetBytes = MAX_UPLOAD_BYTES,
): Promise<{ base64: string; mimeType: SupportedMimeType }> {
  // GIFs: send as-is (canvas loses animation)
  if (file.type === 'image/gif') {
    const base64 = await fileToBase64(file);
    const sizeBytes = Math.ceil(base64.length * 0.75);
    if (sizeBytes > budgetBytes) {
      throw new Error(
        `Image "${file.name}" is too large (${(sizeBytes / 1024).toFixed(0)}KB) — GIFs cannot be recompressed. Max ${(budgetBytes / 1024).toFixed(0)}KB for this turn.`,
      );
    }
    return { base64, mimeType: 'image/gif' };
  }

  const img = await loadImage(file);
  const { width, height } = img;

  // Calculate target dimensions
  let targetW = width;
  let targetH = height;
  if (width > maxWidth) {
    targetW = maxWidth;
    targetH = Math.round(height * (maxWidth / width));
  }

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0, targetW, targetH);
  URL.revokeObjectURL(img.src);

  // Output as JPEG for photos (smaller), keep PNG if source is PNG (transparency)
  const outType: SupportedMimeType =
    file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  let currentQuality = quality;
  let dataUrl = canvas.toDataURL(outType, currentQuality);
  let base64 = dataUrl.split(',')[1];
  let sizeBytes = Math.ceil(base64.length * 0.75);

  // Step-down quality until we fit the budget. Only meaningful for JPEG —
  // PNG is lossless so quality has no effect; if PNG is too big the caller
  // would need a smaller maxWidth. JPEG quality floor at 0.25.
  if (outType === 'image/jpeg') {
    while (sizeBytes > budgetBytes && currentQuality > 0.25) {
      currentQuality = Math.max(0.25, currentQuality - 0.15);
      dataUrl = canvas.toDataURL('image/jpeg', currentQuality);
      base64 = dataUrl.split(',')[1];
      sizeBytes = Math.ceil(base64.length * 0.75);
    }
  }

  if (sizeBytes > budgetBytes) {
    throw new Error(
      `Image "${file.name}" is too large (${(sizeBytes / 1024).toFixed(0)}KB) even after compression. Budget for this turn: ${(budgetBytes / 1024).toFixed(0)}KB.`,
    );
  }

  return { base64, mimeType: outType };
}

/** Create a small thumbnail as a data URI for chat display */
export async function createThumbnail(
  file: File,
  maxWidth = 300,
): Promise<string> {
  if (file.type === 'image/gif') {
    // For GIFs just return a small data URI (loses animation but fine for preview)
    const img = await loadImage(file);
    const ratio = Math.min(maxWidth / img.width, 1);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * ratio);
    canvas.height = Math.round(img.height * ratio);
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(img.src);
    return canvas.toDataURL('image/jpeg', 0.6);
  }

  const img = await loadImage(file);
  const ratio = Math.min(maxWidth / img.width, 1);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.width * ratio);
  canvas.height = Math.round(img.height * ratio);
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  URL.revokeObjectURL(img.src);
  return canvas.toDataURL('image/jpeg', 0.6);
}

/** Convert a File to raw base64 string (no data URI prefix) */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
    reader.readAsDataURL(file);
  });
}
